import jsPDF from 'jspdf';
import { FRAGEN_KATALOG } from '../../components/planungsphase/fragenkatalog';
import { heuteLokalIso, HINWEIS_ART_LABEL } from '../../components/planungsphase/planungLogic';
import type {
    HinweisArt,
    NeuerPlanungshinweis,
    Planungsfrage,
} from '../../components/planungsphase/types';
import { NICHT_ANGEGEBEN } from './fehlendeAngabe';
import {
    A4_GEOMETRY,
    cityFromAddress,
    CompanyInfo,
    createLetterhead,
    createSpaceGuard,
    drawFooters,
    drawPage1Header,
    FOOTER_RESERVE,
} from './letterhead';
import { COLORS } from './pdfUtils';
import { winAnsiSicher } from './winAnsi';

/**
 * Hinweis, Bedenkenanzeige und Gefahrenanzeige der Planungsphase als
 * Geschaeftsbrief (Plan Stufe 4.2).
 *
 * ─── Was das Blatt leisten muss ───────────────────────────────────────────
 * Eine formal schwache Bedenkenanzeige enthaftet nicht. Die Pflichtteile
 * stehen deshalb als eigene, benannte Abschnitte auf dem Blatt: Sachverhalt,
 * Gefaehrdung, konkrete nachteilige Folgen, Rechtsgrundlage, geforderte
 * Entscheidung, Frist und Folge bei Fristablauf. Welche Felder Pflicht sind,
 * entscheidet NICHT dieser Generator, sondern `pruefeHinweis` in
 * `planungLogic.ts` — hier wird nur gesetzt, was dort verlangt wird. Fuer den
 * blossen `hinweis` entfallen die Pflichtabschnitte, wenn sie leer sind; fuer
 * `bedenken` und `gefahr` stehen sie immer, eine Luecke wird benannt.
 *
 * ─── Fehlende Werte werden benannt, nie als Strich gedruckt ──────────────
 * Hausregel (`fehlendeAngabe.ts`): ein „—" liest sich als geprueft-und-ohne-
 * Befund. Auf einer Bedenkenanzeige hiesse eine leere Fristzeile „keine
 * Frist noetig" — das Gegenteil dessen, was die Anzeige wirksam macht.
 *
 * ─── Rechtsgrundlagen kommen aus dem Datensatz ────────────────────────────
 * Gedruckt wird ausschliesslich `hinweis.rechtsgrundlage` (uebernommen aus
 * dem Fragenkatalog oder vom Koordinator eingetragen). Der Generator kennt
 * keine eigenen Paragraphen — eine erfundene Fundstelle auf einem Blatt, das
 * der Haftung dient, waere schlimmer als gar keine.
 *
 * ─── Briefkopf ────────────────────────────────────────────────────────────
 * Wie `generatorPlanungsfragen.ts`: ausschliesslich ueber `letterhead.ts`,
 * umgeblaettert nur ueber `lh.addPage()`, damit ein hinterlegtes Briefpapier
 * auf JEDER Seite landet.
 *
 * ─── Zeichensatz ──────────────────────────────────────────────────────────
 * jsPDF setzt mit den Standardschriften und kann genau CP1252. Freier Text
 * aus der Datenbank geht durch `winAnsiSicher`; die Textbausteine dieser
 * Datei prueft der Waechter in `winAnsi.test.ts`.
 */

// ─── Typografie ─────────────────────────────────────────────────────────────

const SCHRIFT = {
    titel: 15,
    untertitel: 10.5,
    abschnitt: 11,
    block: 9.5,
    klein: 8,
};

const ZEILE = {
    text: 4.4,
    klein: 3.4,
    abschnitt: 5.4,
};

/** Einzug der Fragetexte unter der Codezeile in mm. */
const EINZUG = 4;

/** Platz fuer die handschriftliche Unterschrift in mm. */
const UNTERSCHRIFT_PLATZ = 18;

/** Die Hinweisarten in Anzeigereihenfolge — dieselben wie `HinweisArt`. */
const ARTEN: ReadonlyArray<HinweisArt> = ['hinweis', 'bedenken', 'gefahr'];

/**
 * Was nach dem Betreff eingeleitet wird. Die Saetze nennen bewusst keine
 * Paragraphen — Fundstellen stehen nur im Abschnitt „Rechtsgrundlage" und
 * stammen aus dem Datensatz.
 */
const EINLEITUNG: Record<HinweisArt, string> = {
    hinweis: 'als Koordinator für Sicherheit und Gesundheitsschutz weisen wir Sie auf den nachstehenden '
        + 'Sachverhalt hin und empfehlen die genannte Abhilfe.',
    bedenken: 'als Koordinator für Sicherheit und Gesundheitsschutz zeigen wir Ihnen hiermit förmlich '
        + 'Bedenken an. Die Anzeige richtet sich an Sie als Bauherrn bzw. als den zur Entscheidung '
        + 'Befugten; sie ist Bestandteil der Projektakte.',
    gefahr: 'als Koordinator für Sicherheit und Gesundheitsschutz zeigen wir Ihnen hiermit eine Gefahr an '
        + 'und verlangen, die betroffenen Arbeiten einzustellen bzw. nicht aufzunehmen, bis die '
        + 'nachstehend geforderte Abhilfe getroffen ist.',
};

/** Bezeichnung, wenn der Datensatz keine bekannte Art traegt. */
const ART_UNBEKANNT = `Planungshinweis (Art ${NICHT_ANGEGEBEN})`;

/** Ueberschrift des Fragenabschnitts — der Code ist die Referenz zur Klaerungsliste. */
const FRAGEN_TITEL = 'Betroffene Fragen der Klärungsliste';

/** Ueberschrift des Forderungsabschnitts je Art — wie das Feldlabel in der Maske. */
const FORDERUNG_TITEL: Record<HinweisArt, string> = {
    hinweis: 'Empfohlene Abhilfe',
    bedenken: 'Geforderte Entscheidung oder Abhilfe',
    gefahr: 'Verlangen: Einstellung der Arbeiten',
};

/** Bezeichnung der Art fuer Titel und Kopfdaten; unbekannte Werte werden benannt. */
export const hinweisArtLabel = (art: HinweisArt | string | null | undefined): string => {
    const key = String(art || '');
    return (ARTEN as readonly string[]).includes(key) && HINWEIS_ART_LABEL[key]
        ? HINWEIS_ART_LABEL[key]
        : ART_UNBEKANNT;
};

// ─── Eingaben ───────────────────────────────────────────────────────────────

/**
 * Was der Generator braucht: den Entwurf oder den gespeicherten Satz. Die
 * Nummer ist optional, weil ein noch nicht gespeicherter Entwurf keine hat
 * — auf dem Blatt steht dann, dass sie beim Speichern vergeben wird.
 */
export type PlanungshinweisPdfDaten = NeuerPlanungshinweis & {
    nummer?: number | null;
};

export interface PlanungshinweisPdfOptionen {
    projektName?: string;
    projektNummer?: string;
    bauvorhabenAdresse?: string;
    /** Postanschrift des Empfaengers unter dem Namen, zeilenweise. */
    empfaengerAnschrift?: string[];
    /** Ort fuer die Ort/Datum-Zeile; ohne Angabe aus der Firmenadresse. */
    ort?: string;
    /**
     * Datum des Blattes als ISO-Tag (YYYY-MM-DD, Ortszeit). Ohne Angabe gilt
     * `versendet_am` (als Ortstag) und sonst der heutige Tag — ein spaeter
     * erneut erzeugtes Blatt traegt so das Datum des Versands.
     */
    datumIso?: string;
    /**
     * Fragen des Projekts, damit zu jedem Code der Fragetext steht. Fehlt
     * eine Frage hier, greift der Katalog; eigene Fragen (`PL-X-…`) kennt nur
     * diese Liste.
     */
    fragen?: ReadonlyArray<Pick<Planungsfrage, 'code' | 'frage'>>;
    /**
     * Eigenes jsPDF-Objekt statt eines frisch erzeugten. Nur fuer Tests —
     * dasselbe Muster wie in `generatorPlanungsfragen.ts`.
     */
    __docFactory?: () => jsPDF;
}

// ─── Kleinkram ──────────────────────────────────────────────────────────────

/** Mehrzeiliges Feld zu einer Zeile — Kopfangaben sind einzeilig. */
const einzeilig = (v?: string | null): string =>
    (v == null ? '' : String(v)).replace(/\s*\n\s*/g, ', ').replace(/\s+/g, ' ').trim();

/** Getrimmter Wert oder die benannte Luecke. */
const oder = (v: string | null | undefined, sonst: string = NICHT_ANGEGEBEN): string => {
    const t = (v == null ? '' : String(v)).trim();
    return t || sonst;
};

/**
 * ISO-Tag → TT.MM.JJJJ durch reines Zerlegen. Kein Date-Parsing: das
 * verschiebt ein Tagesdatum je nach Zeitzone um einen Tag. Leer bei
 * unbrauchbarer Eingabe.
 */
const tagDeutsch = (iso: string | null | undefined): string => {
    const t = String(iso || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return '';
    const [j, m, d] = t.split('-');
    return `${d}.${m}.${j}`;
};

/**
 * ISO-Tag → „17. Oktober 2026". Aus den Tagesteilen in Ortszeit gebaut,
 * damit der Tag nicht an der Zeitzone kippt (`longDate` aus `letterhead.ts`
 * parst den String als UTC-Mitternacht).
 */
const tagLang = (iso: string): string => {
    const t = String(iso || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return '';
    const [j, m, d] = t.split('-').map(Number);
    return new Date(j, m - 1, d).toLocaleDateString('de-DE', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
};

/** Zeitstempel (ISO, UTC) → Ortstag. Leer bei unbrauchbarer Eingabe. */
const ortstagAus = (zeitstempel: string | null | undefined): string => {
    const t = Date.parse(String(zeitstempel || ''));
    return Number.isFinite(t) ? heuteLokalIso(new Date(t)) : '';
};

/**
 * Datum des Blattes: ausdrueckliche Angabe, sonst der Ortstag des Versands,
 * sonst heute. Exportiert, damit Dateiname und Blatt dasselbe Datum tragen.
 */
export const hinweisBlattDatum = (
    hinweis: Pick<PlanungshinweisPdfDaten, 'versendet_am'>,
    datumIso?: string,
): string => {
    const ausdruecklich = String(datumIso || '').slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(ausdruecklich)) return ausdruecklich;
    return ortstagAus(hinweis ? hinweis.versendet_am : null) || heuteLokalIso();
};

/**
 * Fragetext zu einem Code — aus der Projektliste, sonst aus dem Katalog.
 * Fehlt beides, wird das benannt: eine Codezeile ohne Text saehe wie ein
 * Verweis auf etwas Bekanntes aus.
 */
export const fragetextZuCode = (
    code: string,
    fragen?: ReadonlyArray<Pick<Planungsfrage, 'code' | 'frage'>>,
): string => {
    const gesucht = (code || '').trim();
    const eigene = (fragen || []).find((f) => f && (f.code || '').trim() === gesucht);
    if (eigene && (eigene.frage || '').trim()) return eigene.frage.trim();
    const katalog = FRAGEN_KATALOG.find((f) => f.code === gesucht);
    if (katalog && katalog.frage) return katalog.frage;
    return 'Fragetext nicht hinterlegt';
};

/** Eine gesetzte Zeile eines Blocks. */
interface Satzzeile {
    text: string;
    stil: 'normal' | 'bold' | 'italic';
    groesse: number;
    hoehe: number;
    einzug: number;
}

// ─── Der Satz ───────────────────────────────────────────────────────────────

const renderPlanungshinweis = (
    doc: jsPDF,
    hinweis: PlanungshinweisPdfDaten,
    company: CompanyInfo,
    optionen?: PlanungshinweisPdfOptionen,
): void => {
    const opt: PlanungshinweisPdfOptionen = optionen || {};
    const h: PlanungshinweisPdfDaten = hinweis || ({} as PlanungshinweisPdfDaten);
    const geo = A4_GEOMETRY;
    const { margin, contentWidth, rightX } = geo;

    const [tp0, tp1, tp2] = COLORS.text.primary;
    const [ln0, ln1, ln2] = COLORS.section.line;

    const art: HinweisArt = (ARTEN as readonly string[]).includes(String(h.art)) ? h.art : 'hinweis';
    const artBekannt = (ARTEN as readonly string[]).includes(String(h.art));
    const foermlich = art !== 'hinweis';
    const datumIso = hinweisBlattDatum(h, opt.datumIso);

    const lh = createLetterhead(doc, company, geo);
    lh.stampCurrentBg();
    const { addressFieldY, rightColBottom } = drawPage1Header(doc, company, lh, geo);

    let y = 0;

    const ensureSpace = createSpaceGuard(
        lh,
        geo,
        () => y,
        (v) => {
            y = v;
        },
    );

    // `createSpaceGuard` misst gegen die Blattkante; ein mehrzeiliger Block
    // muss seine Hoehe draufrechnen, sonst landen seine letzten Zeilen in
    // der Fusszeile (siehe `generatorPlanungsfragen.ts`).
    const fussReserve = lh.hasStationery ? lh.botMargin : FOOTER_RESERVE;
    const platzFuer = (hoehe: number) => ensureSpace(hoehe + fussReserve);
    const nutzbareHoehe = geo.pageHeight - lh.contentTopFollowing - fussReserve;

    // ── Anschriftfeld ────────────────────────────────────────────────────
    // Der Empfaenger steht IMMER — fehlt der Name, steht die Luecke dort,
    // wo der Leser den Adressaten sucht.
    let addrY = addressFieldY + 5;
    doc.setTextColor(tp0, tp1, tp2);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const anschrift: string[] = [oder(h.empfaenger_name, `Empfänger ${NICHT_ANGEGEBEN}`)];
    if (h.adressat_ist_bauherr) anschrift.push('Bauherr');
    (opt.empfaengerAnschrift || [])
        .map((z) => (z == null ? '' : String(z)).trim())
        .filter(Boolean)
        .forEach((z) => anschrift.push(z));
    const email = (h.empfaenger_email || '').trim();
    if (email) anschrift.push(`per E-Mail: ${email}`);
    anschrift.forEach((zeile) => {
        (doc.splitTextToSize(winAnsiSicher(zeile), 85) as string[]).forEach((z) => {
            doc.text(z, margin, addrY);
            addrY += 5;
        });
    });

    // ── Ort und Datum ────────────────────────────────────────────────────
    y = Math.max(addrY + 6, addressFieldY + 30, rightColBottom + 8);
    const ort = (opt.ort || '').trim() || cityFromAddress(company ? company.address : undefined);
    const datumText = tagLang(datumIso);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(SCHRIFT.untertitel);
    doc.setTextColor(tp0, tp1, tp2);
    doc.text(winAnsiSicher(ort ? `${ort}, ${datumText}` : datumText), rightX, y, { align: 'right' });
    y += 12;

    // ── Titel ────────────────────────────────────────────────────────────
    const nummer = typeof h.nummer === 'number' && Number.isFinite(h.nummer) ? h.nummer : null;
    const titel = nummer !== null
        ? `${hinweisArtLabel(h.art)} Nr. ${nummer}`
        : hinweisArtLabel(h.art);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(SCHRIFT.titel);
    (doc.splitTextToSize(winAnsiSicher(titel), contentWidth) as string[]).forEach((z) => {
        doc.text(z, margin, y);
        y += 7;
    });

    // ── Betreff ──────────────────────────────────────────────────────────
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(SCHRIFT.untertitel);
    (doc.splitTextToSize(winAnsiSicher(`Betreff: ${oder(einzeilig(h.betreff))}`), contentWidth) as string[])
        .forEach((z) => {
            doc.text(z, margin, y);
            y += 5;
        });

    // ── Kopfdaten: Projekt, Nummer, Datum, Art ───────────────────────────
    const kopf: string[] = [];
    const projektName = einzeilig(opt.projektName);
    const projektNummer = einzeilig(opt.projektNummer);
    if (projektName && projektNummer) kopf.push(`Projekt: ${projektName} (Projekt-Nr. ${projektNummer})`);
    else if (projektName) kopf.push(`Projekt: ${projektName}`);
    else if (projektNummer) kopf.push(`Projekt-Nr. ${projektNummer}`);
    else kopf.push(`Projekt: ${NICHT_ANGEGEBEN}`);
    kopf.push(`Bauvorhaben: ${oder(einzeilig(opt.bauvorhabenAdresse))}`);
    kopf.push(`Nummer: ${nummer !== null ? String(nummer) : 'wird beim Speichern vergeben'}`);
    kopf.push(`Datum: ${tagDeutsch(datumIso) || NICHT_ANGEGEBEN}`);
    kopf.push(`Art: ${artBekannt ? HINWEIS_ART_LABEL[art] : NICHT_ANGEGEBEN}`);
    kopf.push(`Erstellt von: ${oder(einzeilig(h.erstellt_von_name))}`);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(SCHRIFT.untertitel);
    kopf.forEach((zeile) => {
        (doc.splitTextToSize(winAnsiSicher(zeile), contentWidth) as string[]).forEach((z) => {
            doc.text(z, margin, y);
            y += 5;
        });
    });

    y += 1.5;
    doc.setDrawColor(ln0, ln1, ln2);
    doc.setLineWidth(0.2);
    doc.line(margin, y, rightX, y);
    y += 6;

    // ─────────────────────────────────────────────────────────────────────
    // Werkzeug: Zeilen bauen, Bloecke ungetrennt setzen
    // ─────────────────────────────────────────────────────────────────────

    const zeilenAus = (
        text: string,
        stil: 'normal' | 'bold' | 'italic',
        groesse: number,
        hoehe: number,
        einzug = 0,
    ): Satzzeile[] => {
        const roh = winAnsiSicher((text == null ? '' : String(text)).trim());
        if (!roh) return [];
        // splitTextToSize misst mit der GERADE gesetzten Schrift — deshalb
        // erst stellen, dann umbrechen.
        doc.setFont('helvetica', stil);
        doc.setFontSize(groesse);
        return (doc.splitTextToSize(roh, contentWidth - einzug) as string[]).map((z) => ({
            text: z,
            stil,
            groesse,
            hoehe,
            einzug,
        }));
    };

    const zeichneZeile = (z: Satzzeile) => {
        doc.setFont('helvetica', z.stil);
        doc.setFontSize(z.groesse);
        doc.setTextColor(tp0, tp1, tp2);
        doc.text(z.text, margin + z.einzug, y);
        y += z.hoehe;
    };

    /**
     * Setzt einen Block als Einheit: passt er auf eine leere Seite, wird er
     * NIE zerrissen. Ist er laenger als eine Seite, bricht er zeilenweise um
     * — abgeschnitten wird nichts.
     */
    const zeichneBlock = (zeilen: Satzzeile[], nachAbstand = 0) => {
        const echte = (zeilen || []).filter(Boolean);
        if (echte.length === 0) return;
        const hoehe = echte.reduce((s, z) => s + z.hoehe, 0);
        if (hoehe <= nutzbareHoehe) {
            platzFuer(hoehe);
            echte.forEach(zeichneZeile);
        } else {
            echte.forEach((z) => {
                platzFuer(z.hoehe);
                zeichneZeile(z);
            });
        }
        y += nachAbstand;
    };

    /** Absaetze eines Freitextfelds — jeder Absatz darf umbrechen, die
     *  Leerzeile zwischen zweien soll nicht allein auf die Folgeseite. */
    const absaetze = (text: string): string[] =>
        (text || '')
            .replace(/\r\n/g, '\n')
            .split(/\n{2,}/)
            .map((a) => a.replace(/\s*\n\s*/g, ' ').trim())
            .filter(Boolean);

    /**
     * Ein Abschnitt mit Ueberschrift und Freitext. Die Ueberschrift nimmt
     * die erste Textzeile mit, damit sie nie allein am Seitenfuss steht.
     * Leerer Text wird benannt (`luecke`), nie weggelassen.
     */
    const abschnitt = (ueberschrift: string, text: string, luecke: string = NICHT_ANGEGEBEN) => {
        const teile = absaetze(text);
        const kopfZeilen = zeilenAus(ueberschrift, 'bold', SCHRIFT.abschnitt, ZEILE.abschnitt);
        if (teile.length === 0) {
            zeichneBlock(kopfZeilen.concat(zeilenAus(luecke, 'italic', SCHRIFT.block, ZEILE.text)), 4);
            return;
        }
        teile.forEach((absatz, i) => {
            const zeilen = zeilenAus(absatz, 'normal', SCHRIFT.block, ZEILE.text);
            zeichneBlock(i === 0 ? kopfZeilen.concat(zeilen) : zeilen, i === teile.length - 1 ? 4 : 2.5);
        });
    };

    // ── Anrede und Einleitung ────────────────────────────────────────────
    zeichneBlock(zeilenAus('Sehr geehrte Damen und Herren,', 'normal', SCHRIFT.block, ZEILE.text), 2.5);
    zeichneBlock(zeilenAus(EINLEITUNG[art], 'normal', SCHRIFT.block, ZEILE.text), 5);

    // ── Pflichtteile ─────────────────────────────────────────────────────
    // Fuer `hinweis` entfallen leere Abschnitte — `pruefeHinweis` verlangt
    // sie dort nicht. Fuer `bedenken` und `gefahr` stehen sie immer.
    const wennFoermlichOderGefuellt = (ueberschrift: string, text: string, luecke?: string) => {
        if (foermlich || (text || '').trim()) abschnitt(ueberschrift, text, luecke);
    };

    abschnitt('Sachverhalt', h.sachverhalt);
    wennFoermlichOderGefuellt('Gefährdung', h.gefaehrdung);
    wennFoermlichOderGefuellt('Konkrete nachteilige Folgen', h.folgen);

    // ── Betroffene Fragen der Klaerungsliste ─────────────────────────────
    const codes = (h.frage_codes || []).map((c) => (c || '').trim()).filter(Boolean);
    if (codes.length > 0) {
        let block = zeilenAus(FRAGEN_TITEL, 'bold', SCHRIFT.abschnitt, ZEILE.abschnitt);
        codes.forEach((code) => {
            block = block
                .concat(zeilenAus(code, 'bold', SCHRIFT.block, ZEILE.text))
                .concat(zeilenAus(fragetextZuCode(code, opt.fragen), 'normal', SCHRIFT.block, ZEILE.text, EINZUG));
        });
        zeichneBlock(block, 4);
    } else if (foermlich) {
        abschnitt(FRAGEN_TITEL, '', 'ohne Bezug auf eine Frage der Klärungsliste');
    }

    // ── Rechtsgrundlage — nur aus dem Datensatz ──────────────────────────
    const rechte = (h.rechtsgrundlage || []).map((r) => (r || '').trim()).filter(Boolean);
    if (rechte.length > 0) {
        const block = zeilenAus('Rechtsgrundlage', 'bold', SCHRIFT.abschnitt, ZEILE.abschnitt)
            .concat(rechte.flatMap((r) => zeilenAus(r, 'normal', SCHRIFT.block, ZEILE.text, EINZUG)));
        zeichneBlock(block, 4);
    } else if (foermlich) {
        abschnitt('Rechtsgrundlage', '');
    }

    // ── Forderung, Frist, Folge bei Fristablauf ──────────────────────────
    wennFoermlichOderGefuellt(FORDERUNG_TITEL[art], h.forderung);

    const frist = tagDeutsch(h.frist_am);
    const folge = (h.folge_bei_fristablauf || '').trim();
    if (foermlich || frist || folge) {
        const bitte = frist
            ? `Wir bitten um Ihre schriftliche Stellungnahme und Entscheidung bis zum ${frist}.`
            : `Wir bitten um Ihre schriftliche Stellungnahme und Entscheidung. Frist zur Rückäußerung: ${NICHT_ANGEGEBEN}.`;
        let block = zeilenAus('Bitte um Stellungnahme', 'bold', SCHRIFT.abschnitt, ZEILE.abschnitt)
            .concat(zeilenAus(bitte, 'normal', SCHRIFT.block, ZEILE.text));
        block = block.concat(
            zeilenAus(`Folge bei Fristablauf: ${oder(folge)}`, 'normal', SCHRIFT.block, ZEILE.text),
        );
        zeichneBlock(block, 4);
    }

    // ── Verteiler ────────────────────────────────────────────────────────
    const verteiler = (h.verteiler || []).map((v) => (v || '').trim()).filter(Boolean);
    if (verteiler.length > 0) {
        zeichneBlock(
            zeilenAus(`Verteiler: ${verteiler.join('; ')}`, 'italic', SCHRIFT.klein, ZEILE.klein),
            4,
        );
    }

    // ── Gruss und Unterschrift ───────────────────────────────────────────
    // Gruss, Unterschriftsraum und Namenszeile bleiben zusammen — eine
    // Unterschrift ohne Gruss auf der Folgeseite sieht nach Anhang aus.
    const firma = einzeilig(company ? company.name : '');
    const name = oder(einzeilig(h.erstellt_von_name), `Name ${NICHT_ANGEGEBEN}`);
    const grussZeilen = zeilenAus('Mit freundlichen Grüßen', 'normal', SCHRIFT.block, ZEILE.text)
        .concat(firma ? zeilenAus(firma, 'normal', SCHRIFT.block, ZEILE.text) : []);
    const namensZeilen = zeilenAus(name, 'normal', SCHRIFT.block, ZEILE.text)
        .concat(zeilenAus('Koordinator für Sicherheit und Gesundheitsschutz', 'italic', SCHRIFT.klein, ZEILE.klein));
    const unterschriftHoehe = grussZeilen.reduce((s, z) => s + z.hoehe, 0)
        + UNTERSCHRIFT_PLATZ
        + namensZeilen.reduce((s, z) => s + z.hoehe, 0)
        + ZEILE.klein;
    platzFuer(unterschriftHoehe);
    y += 2;
    grussZeilen.forEach(zeichneZeile);
    y += UNTERSCHRIFT_PLATZ;
    doc.setDrawColor(ln0, ln1, ln2);
    doc.setLineWidth(0.2);
    doc.line(margin, y - 1.5, margin + 80, y - 1.5);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(SCHRIFT.klein);
    doc.setTextColor(tp0, tp1, tp2);
    doc.text('Unterschrift', margin, y + 1.5);
    y += ZEILE.klein + 2;
    namensZeilen.forEach(zeichneZeile);

    // Fusszeile und Seitenzahl zuletzt — vorher steht die Gesamtzahl nicht fest.
    drawFooters(doc, company, lh, geo);
};

/**
 * Hinweis, Bedenkenanzeige oder Gefahrenanzeige als jsPDF-Dokument.
 *
 * Gibt bewusst das Dokument zurueck und keine Bytes: der Aufrufer entscheidet,
 * ob er anzeigt, ablegt oder verschickt.
 */
export const generatePlanungshinweisPdf = (
    hinweis: PlanungshinweisPdfDaten,
    company: CompanyInfo,
    optionen?: PlanungshinweisPdfOptionen,
): jsPDF => {
    const doc =
        optionen && optionen.__docFactory
            ? optionen.__docFactory()
            : new jsPDF({ format: 'a4', unit: 'mm', compress: true });
    renderPlanungshinweis(doc, hinweis, company || {}, optionen);
    return doc;
};
